import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getVehicles } from "@/api/vehicles";
import { listUsers } from "@/api/users";
import { useAuthStore } from "@/store/auth";
import {
  ANALYTICS_TABS,
  analyticsDefaultDateFrom,
  analyticsDefaultDateTo,
} from "@/features/reports/analyticsFilters";
import type { AnalyticsTab } from "@/features/reports/analyticsFilters";
import { AnalyticsTabBar } from "@/features/reports/components/AnalyticsTabBar";
import { AnalyticsFilterBar } from "@/features/reports/components/AnalyticsFilterBar";
import type { AnalyticsFilterValues } from "@/features/reports/components/AnalyticsFilterBar";
import { DashboardTab } from "@/features/dashboard/components/DashboardTab";
import { ReviewsTab } from "@/features/feedback/components/ReviewsTab";
import { DriversReportTab } from "@/features/reports/components/DriversReportTab";
import { PlanVsActualTab } from "@/features/reports/components/PlanVsActualTab";
import { FleetLoadTab } from "@/features/reports/components/FleetLoadTab";
import { NotificationsLogTab } from "@/features/reports/components/NotificationsLogTab";
import { ReportsHubTab } from "@/features/reports/components/ReportsHubTab";
import { usePageHeader } from "@/features/shell/hooks/usePageHeader";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import type { UrlFiltersConfig } from "@/hooks/useUrlFilters";
import type { ReportParams } from "@/types/reports";

const TAB_SET = new Set<string>(ANALYTICS_TABS);

/** Default tab when a `?tab=` slug is absent or cannot be resolved. */
const DEFAULT_TAB: AnalyticsTab = "dashboard";

/**
 * Aliases for slightly-wrong or legacy `?tab=` slugs. Each key maps a near-miss
 * to its canonical {@link AnalyticsTab} so a stale bookmark or an obvious typo
 * lands on the intended tab instead of silently dropping to the Dashboard.
 * Canonical slugs (no alias needed): `dashboard`, `plan-vs-actual`, `drivers`,
 * `reviews`, `fleet-load`, `notifications`, `reports`.
 */
const TAB_ALIASES: Record<string, AnalyticsTab> = {
  fleet: "fleet-load",
  fleetload: "fleet-load",
  "plan-actual": "plan-vs-actual",
  planvsactual: "plan-vs-actual",
  "plan-vs-actuals": "plan-vs-actual",
  feedback: "reviews",
  reviews: "reviews",
  notification: "notifications",
};

/**
 * Resolves a raw `?tab=` slug to a known {@link AnalyticsTab}. Order: exact
 * canonical match → alias map → Dashboard fallback. The fallback is deliberate
 * (a genuinely-unknown slug is not redirected, just defaulted) and the whole
 * mapping is explicit so the behavior is testable rather than incidental.
 */
function resolveTab(raw: string | null | undefined): AnalyticsTab {
  if (raw == null) return DEFAULT_TAB;
  const key = raw.trim().toLowerCase();
  if (TAB_SET.has(key)) return key as AnalyticsTab;
  return TAB_ALIASES[key] ?? DEFAULT_TAB;
}

/**
 * Every Analytics URL-filter param whose default does NOT depend on the clock.
 * Spread into the hook's config beside the two date params below.
 */
const ANALYTICS_STATIC_FILTERS: UrlFiltersConfig = {
  // Active tab — canonical slugs and known aliases resolve here; anything else
  // falls back to the Dashboard tab (see resolveTab).
  tab: {
    defaultValue: DEFAULT_TAB,
    parse: (raw) => resolveTab(raw),
  },
  // Shared filters.
  vehicle_id: { defaultValue: "" },
  driver_id: { defaultValue: "" },
  preset: { defaultValue: "days30" },
  // Reviews tab params (date_from/date_to live beside this block in
  // useAnalyticsFiltersConfig; driver_id is shared above).
  rating: { defaultValue: "" },
  has_comment: { defaultValue: "" },
  search: { defaultValue: "" },
  page: { defaultValue: "1" },
  // Notifications tab params — namespaced to avoid colliding with Reviews.
  channel: { defaultValue: "" },
  status: { defaultValue: "" },
  undelivered: { defaultValue: "" },
  n_search: { defaultValue: "" },
  n_page: { defaultValue: "1" },
};

/**
 * Single URL-filters config for the whole Analytics page. It holds the active
 * tab plus every param the hosted tabs read so the page is fully deep-linkable
 * and reload-safe. Shared date/vehicle/driver filters live alongside the
 * Reviews tab's own params and the namespaced (`n_*`) Notifications params on a
 * single query string.
 *
 * TASK-5041: the config used to be a module-level constant whose two date
 * defaults were UTC-sliced strings frozen at IMPORT — so a tab left open across
 * midnight kept filtering the previous day, and for Europe/Minsk the window
 * named yesterday for the first three hours of every local day. It is now
 * derived from the tenant timezone, which only a hook can supply.
 *
 * The identity still has to be STABLE: `useUrlFilters` lists `config` in the
 * deps of its `useMemo`/`useCallback`, so a fresh object every render would
 * re-run all of that on each keystroke. The two date strings are recomputed on
 * every render (that is what makes a midnight rollover visible) and the OBJECT
 * is memoised on them — so it changes exactly twice: when the tenant's zone
 * resolves, and at the tenant's midnight. Same shape as
 * `useRoutesFiltersConfig` in `RoutesPage.tsx` (BUG-530).
 */
function useAnalyticsFiltersConfig(tz: string): UrlFiltersConfig {
  const dateFromDefault = analyticsDefaultDateFrom(tz);
  const dateToDefault = analyticsDefaultDateTo(tz);

  return useMemo<UrlFiltersConfig>(
    () => ({
      date_from: { defaultValue: dateFromDefault },
      date_to: { defaultValue: dateToDefault },
      ...ANALYTICS_STATIC_FILTERS,
    }),
    [dateFromDefault, dateToDefault],
  );
}

// Tabs that show the shared date/vehicle/driver filter bar.
const FILTER_BAR_TABS = new Set<AnalyticsTab>([
  "plan-vs-actual",
  "drivers",
  "fleet-load",
  "reports",
]);

// Default visible columns for the Drivers scorecard table when hosted here.
const DRIVER_VISIBLE_KEYS = [
  "driver",
  "routes",
  "completedOrders",
  "completedPct",
  "onTime",
  "avgLate",
  "avgService",
  "plannedKm",
  "actualKm",
  "sites",
  "rating",
];

/**
 * Consolidated Analytics page (Sprint 117, TASK-3828).
 *
 * Replaces the separate `/dashboard`, `/reports`, and `/feedback` routes with a
 * single tabbed page. The active tab is URL-persisted via `?tab=`; a shared
 * date/vehicle/driver filter bar is shown on the tabs that need it. Dashboard,
 * Drivers, and Reviews reuse their existing bodies; Planned-vs-Actual, Fleet
 * Load, Notifications, and Reports are placeholder stubs fleshed out by
 * TASK-3829..3832.
 */
export default function AnalyticsPage() {
  const { t } = useTranslation();
  usePageHeader({ title: t("analytics.title") });

  /* Read per render, never latched: the hook serves the `Europe/Minsk` fallback
     while `["settings-planning"]` loads, so a captured value would bake the
     fallback's day into the filter defaults for a non-Minsk tenant (the hazard
     `RouteFormModal.tsx:406-424` documents). */
  const tenantTz = useTenantTimezone();
  const filtersConfig = useAnalyticsFiltersConfig(tenantTz);
  const { values, setValues } = useUrlFilters(filtersConfig);
  const activeTab = values.tab as AnalyticsTab;

  // Dispatcher cannot call GET /users/ (list action requires company_admin+).
  // Gate all vehicle/driver queries so no 403 fires for that role (BUG-219).
  const userRole = useAuthStore((s) => s.user?.role);
  const canListUsers = userRole !== "dispatcher";

  // Vehicle + driver options for the shared filter bar — only needed on tabs
  // that show the filter bar and only for roles that can access those endpoints.
  const needsFilterBar =
    canListUsers &&
    (FILTER_BAR_TABS.has(activeTab as AnalyticsTab) || activeTab === "reviews");

  const { data: vehiclesData } = useQuery({
    queryKey: ["vehicles"],
    queryFn: () => getVehicles(),
    enabled: needsFilterBar,
  });
  const vehicleOptions = (vehiclesData?.results ?? []).map((v) => ({
    id: v.id,
    name: v.name,
  }));

  const { data: driversData } = useQuery({
    queryKey: ["users", "drivers"],
    queryFn: () => listUsers({ role: "driver", page_size: 200 }),
    enabled: needsFilterBar,
  });
  const driverOptions = (driversData?.results ?? []).map((u) => ({
    id: u.id,
    name: `${u.first_name} ${u.last_name}`.trim() || u.email,
  }));

  // Shared report params derived from the URL filters.
  const reportParams = useMemo<ReportParams>(() => {
    const params: ReportParams = {
      date_from: values.date_from,
      date_to: values.date_to,
    };
    if (values.vehicle_id) params.vehicle_id = Number(values.vehicle_id);
    if (values.driver_id) params.driver_id = Number(values.driver_id);
    return params;
  }, [values.date_from, values.date_to, values.vehicle_id, values.driver_id]);

  const filterValues: AnalyticsFilterValues = {
    date_from: values.date_from,
    date_to: values.date_to,
    vehicle_id: values.vehicle_id,
    driver_id: values.driver_id,
    preset: values.preset,
  };

  const showFilterBar = FILTER_BAR_TABS.has(activeTab);

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <AnalyticsTabBar
        active={activeTab}
        onChange={(tab) => setValues({ tab }, { push: true })}
      />

      {/* Shared filter bar — only on the tabs that use it. */}
      {showFilterBar && (
        <AnalyticsFilterBar
          values={filterValues}
          vehicleOptions={vehicleOptions}
          driverOptions={driverOptions}
          onChange={(patch) => setValues(patch)}
          hideDriver={activeTab === "fleet-load"}
          tenantTz={tenantTz}
        />
      )}

      {/* Active tab body */}
      {activeTab === "dashboard" && <DashboardTab />}
      {activeTab === "plan-vs-actual" && (
        <PlanVsActualTab params={reportParams} />
      )}
      {activeTab === "drivers" && (
        <DriversReportTab
          params={reportParams}
          visibleKeys={DRIVER_VISIBLE_KEYS}
        />
      )}
      {activeTab === "reviews" && (
        <ReviewsTab
          values={{
            rating: values.rating,
            date_from: values.date_from,
            date_to: values.date_to,
            driver_id: values.driver_id,
            has_comment: values.has_comment,
            search: values.search,
            preset: values.preset,
            page: values.page,
          }}
          setValues={(patch) => setValues(patch)}
        />
      )}
      {activeTab === "fleet-load" && <FleetLoadTab params={reportParams} />}
      {activeTab === "notifications" && (
        <NotificationsLogTab
          values={{
            channel: values.channel,
            status: values.status,
            undelivered: values.undelivered,
            n_search: values.n_search,
            n_page: values.n_page,
          }}
          setValues={(patch) => setValues(patch)}
        />
      )}
      {activeTab === "reports" && <ReportsHubTab params={reportParams} />}
    </div>
  );
}
